(() => {
  if (window.__dwilMegaMenuRuntime?.init) {
    window.__dwilMegaMenuRuntime.init(document);
    return;
  }

  const menuInstances = new WeakMap();
  const CLOSE_DELAY = 140;
  const desktopPointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const initMenu = (menu) => {
    if (menuInstances.has(menu)) return;

    const controller = new AbortController();
    const { signal } = controller;
    const items = Array.from(menu.querySelectorAll('.modern-nav-item'));
    const closeTimers = new WeakMap();
    let openItem = null;

    const getPopup = (item) =>
      item.querySelector(':scope > .mega-menu-overlay, :scope > .standard-dropdown');

    const cancelClose = (item) => {
      const timer = closeTimers.get(item);
      if (timer) window.clearTimeout(timer);
      closeTimers.delete(item);
    };

    const setOpen = (item, expanded, returnFocus = false) => {
      const menuLink = item.querySelector(':scope > .modern-nav-link');
      const popup = getPopup(item);
      if (!menuLink || !popup) return;

      cancelClose(item);
      item.classList.toggle('is-menu-open', expanded);
      menuLink.setAttribute('aria-expanded', String(expanded));
      popup.setAttribute('aria-hidden', String(!expanded));
      if (expanded) {
        popup.removeAttribute('inert');
      } else {
        popup.setAttribute('inert', '');
      }

      if (expanded) {
        if (openItem && openItem !== item) setOpen(openItem, false);
        openItem = item;
      } else if (openItem === item) {
        openItem = null;
      }

      if (returnFocus) menuLink.focus();
    };

    const closeAll = () => {
      items.forEach((item) => {
        cancelClose(item);
        if (item.classList.contains('is-menu-open')) setOpen(item, false);
      });
    };

    const scheduleClose = (item) => {
      cancelClose(item);
      closeTimers.set(
        item,
        window.setTimeout(() => {
          setOpen(item, false);
          closeTimers.delete(item);
        }, CLOSE_DELAY)
      );
    };

    const activateTab = (item, trigger, moveFocus = false) => {
      const triggers = Array.from(item.querySelectorAll('.mega-menu-tab-trigger'));
      const panels = Array.from(item.querySelectorAll('.mega-menu-tab-panel'));
      const targetId = trigger?.dataset.tabTarget;
      if (!targetId) return;

      triggers.forEach((tab) => {
        const active = tab === trigger;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      panels.forEach((panel) => {
        const active = panel.id === targetId;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
        panel.inert = !active;
      });

      if (moveFocus) trigger.focus();
    };

    items.forEach((item) => {
      const menuLink = item.querySelector(':scope > .modern-nav-link');
      const popup = getPopup(item);
      if (!menuLink || !popup) return;

      menuLink.setAttribute('aria-haspopup', 'true');
      menuLink.setAttribute('aria-expanded', 'false');
      popup.setAttribute('aria-hidden', 'true');
      popup.setAttribute('inert', '');

      const triggers = Array.from(item.querySelectorAll('.mega-menu-tab-trigger'));
      const panels = Array.from(item.querySelectorAll('.mega-menu-tab-panel'));

      panels.forEach((panel, index) => {
        panel.setAttribute('role', 'tabpanel');
        if (triggers[index]?.id) panel.setAttribute('aria-labelledby', triggers[index].id);
      });

      const initialTrigger = triggers.find((trigger) => trigger.classList.contains('is-active')) || triggers[0];
      if (initialTrigger) activateTab(item, initialTrigger);

      triggers.forEach((trigger, index) => {
        trigger.addEventListener('pointerenter', () => activateTab(item, trigger), { signal });
        trigger.addEventListener('focus', () => activateTab(item, trigger), { signal });
        trigger.addEventListener('click', () => activateTab(item, trigger), { signal });
        trigger.addEventListener(
          'keydown',
          (event) => {
            let nextIndex = null;

            if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
              nextIndex = (index + 1) % triggers.length;
            } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
              nextIndex = (index - 1 + triggers.length) % triggers.length;
            } else if (event.key === 'Home') {
              nextIndex = 0;
            } else if (event.key === 'End') {
              nextIndex = triggers.length - 1;
            }

            if (nextIndex !== null) {
              event.preventDefault();
              activateTab(item, triggers[nextIndex], true);
            }
          },
          { signal }
        );
      });

      item.addEventListener(
        'pointerenter',
        () => {
          if (!desktopPointer.matches) return;
          cancelClose(item);
          setOpen(item, true);
        },
        { signal }
      );
      item.addEventListener(
        'pointerleave',
        (event) => {
          if (!desktopPointer.matches || item.contains(event.relatedTarget)) return;
          scheduleClose(item);
        },
        { signal }
      );
      item.addEventListener(
        'focusin',
        () => {
          cancelClose(item);
          setOpen(item, true);
        },
        { signal }
      );
      item.addEventListener(
        'focusout',
        (event) => {
          if (!item.contains(event.relatedTarget)) scheduleClose(item);
        },
        { signal }
      );
      item.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(item, false, true);
          } else if (event.key === 'ArrowDown' && document.activeElement === menuLink && triggers.length) {
            event.preventDefault();
            setOpen(item, true);
            activateTab(item, triggers[0], true);
          }
        },
        { signal }
      );
    });

    menu.addEventListener(
      'pointerdown',
      (event) => {
        const item = event.target.closest('.modern-nav-item');
        if (item) cancelClose(item);
      },
      { signal }
    );
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!menu.contains(event.target)) closeAll();
      },
      { signal }
    );
    window.addEventListener('blur', closeAll, { signal });
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) closeAll();
      },
      { signal }
    );
    desktopPointer.addEventListener('change', closeAll, { signal });

    menu.dataset.dwilMenuReady = 'true';
    menuInstances.set(menu, {
      destroy: () => {
        closeAll();
        controller.abort();
        delete menu.dataset.dwilMenuReady;
        menuInstances.delete(menu);
      },
    });
  };

  const initDwilMegaMenus = (root = document) => {
    if (root.matches?.('.dwil-header-menu')) initMenu(root);
    root.querySelectorAll?.('.dwil-header-menu').forEach(initMenu);
  };

  const destroyDwilMegaMenus = (root) => {
    const menus = [];
    if (root.matches?.('.dwil-header-menu')) menus.push(root);
    root.querySelectorAll?.('.dwil-header-menu').forEach((menu) => menus.push(menu));
    menus.forEach((menu) => menuInstances.get(menu)?.destroy());
  };

  window.__dwilMegaMenuRuntime = {
    init: initDwilMegaMenus,
    destroy: destroyDwilMegaMenus,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initDwilMegaMenus(), { once: true });
  } else {
    initDwilMegaMenus();
  }

  document.addEventListener('shopify:section:load', (event) => initDwilMegaMenus(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyDwilMegaMenus(event.target));
})();
